import packageInfo from '../package.json' with { type: "json" };
import { bigIntToVmNumber, binToHex, createVirtualMachineBch, deriveHdPublicKey, generateTransaction, hdPrivateKeyToP2pkhLockingBytecode, hexToBin, hash256, verifyTransactionTokens, CashAddressNetworkPrefix, lockingBytecodeToCashAddress, swapEndianness, encodeTransactionBch, } from "@bitauth/libauth";
import { getTransactionFees, getScriptHash, sumSourceOutputValue, sumSourceOutputTokenAmounts } from '@unspent/tau';
import { COUPON_SERIES, VAULT_SERIES } from "./constant.js";
import template from './template.v2.json' with { type: "json" };
import { getAllUnspentCoupons, getRates, getRateLocale, getFutureBlockDateLocale } from "./util.js";
import { Coupon } from './coupon.js';
import Future from './auth.js';
export class Vault {
    static USER_AGENT = packageInfo.name;
    static tokenAware = true;
    static compiler = Future.compiler;
    static template = template;
    static vm = createVirtualMachineBch();
    locktime = 0;
    //static unlockingScript = "c0d3c0d0a06376b17568c0cec0d188c0cdc0c788c0d0c0c693c0d3c0cc939c77"
    /**
     * return the scripthash
     *
     * @param time - block time of the vault lock
     * @param reversed - if the result should be reversed; default (electrumX): true.
     * @returns the double scripthash as a string.
     */
    static getScriptHash(time, reversed = true) {
        return getScriptHash(this.getLockingBytecode(time), reversed);
    }
    /**
     * Return the token address for a Vault
     *
     *
     * @param time - block time of the vault lock
     * @param network - cashaddress network prefix
     */
    static getAddress(time, network = CashAddressNetworkPrefix.mainnet, tokenSupport = this.tokenAware) {
        let lockingBytecode = this.getLockingBytecode(time);
        let result = lockingBytecodeToCashAddress({ bytecode: lockingBytecode, prefix: network, tokenSupport: tokenSupport });
        if (typeof result === 'string')
            throw (result);
        return result.address;
    }
    /**
     * Return the coupon for a Vault at specified amount
     *
     *
     * @param amount - the threshold amount (sats) to redeem coupon
     * @param time - block time of the vault lock
     */
    static getCouponAddress(amount, time, network = CashAddressNetworkPrefix.mainnet) {
        return Coupon.getAddress(amount, this.getLockingBytecode(time), network);
    }
    /**
     * Return the coupon for a Vault at specified amount
     *
     *
     * @param amount - the threshold amount (sats) to redeem coupon
     * @param time - block time of the vault lock
     */
    static getCouponScriptHash(amount, time) {
        return Coupon.getScriptHash(amount, this.getLockingBytecode(time));
    }
    /**
     * Return the coupon for a Vault at specified amount
     *
     *
     * @param amount - the threshold amount (sats) to redeem coupon
     * @param time - block time of the vault lock
     */
    static getCouponLockingBytecode(amount, time) {
        return Coupon.getLockingBytecode(amount, this.getLockingBytecode(time));
    }
    /**
     * Return the unlockingBytecode for a Vault
     *
     *
     * @param time - block time of the vault lock
     */
    static getUnlockingBytecode(time) {
        const bytecodeResult = this.compiler.generateBytecode({
            data: {
                "bytecode": {
                    "locktime": bigIntToVmNumber(BigInt(time)),
                }
            },
            scriptId: 'vault_unlock',
        });
        if (!bytecodeResult.success) {
            /* c8 ignore next */
            throw new Error('Failed to generate bytecode, script: FutureChan, ' + JSON.stringify(bytecodeResult, null, '  '));
        }
        return bytecodeResult.bytecode.slice(1);
    }
    /**
     * Return the lockingBytecode for a Vault
     *
     *
     * @param time - block time of the vault lock
     */
    static getLockingBytecode(time) {
        const bytecodeResult = this.compiler.generateBytecode({
            data: {
                "bytecode": {
                    "locktime": bigIntToVmNumber(BigInt(time)),
                }
            },
            scriptId: 'vault_lock',
        });
        if (!bytecodeResult.success) {
            /* c8 ignore next */
            throw new Error('Failed to generate bytecode, script: , ' + JSON.stringify(bytecodeResult, null, '  '));
        }
        return bytecodeResult.bytecode;
    }
    /**
     * Return an array of staggered block times
     *
     *
     * @param startTime - block time of the vault lock
     * @param series - power of 10 to stagger the times
     * @param limit - length of the array to return
     */
    static getSeriesTimes(startTime, series = 3, limit = 11) {
        const step = Math.pow(10, series);
        const next = startTime - (startTime % step) + step;
        //@ts-ignore
        return Array.from({ length: limit }, (e, i) => next + (step * i));
    }
    static getAllCouponSeries(startTime, seriesTimes) {
        if (!seriesTimes)
            seriesTimes = VAULT_SERIES.map(e => this.getSeriesTimes(startTime - 1000, e, e == 3 ? 20 : e == 6 ? 5 : undefined)).flat();
        seriesTimes = [...new Set(seriesTimes)];
        let amounts = COUPON_SERIES.map(c => Math.pow(10, c) * 1e8);
        let coupons = amounts.map(amount => seriesTimes.map((time) => {
            let scripthash = Coupon.getScriptHash(amount, this.getLockingBytecode(time));
            return {
                locktime: time,
                placement: amount,
                order: Math.log10(amount / 1e8),
                scripthash: scripthash,
                lockingBytecode: binToHex(Coupon.getLockingBytecode(amount, this.getLockingBytecode(time)))
            };
        })).flat();
        var map = new Map();
        coupons.forEach(obj => map.set(obj.scripthash, obj));
        return map;
    }
    /**
     * Return an array coupons for vaults in a series
     *
     *
     * @param electrumClient - an v4 electrum-cash client
     * @param height - the height after which to list coupons.
     * @param locktime - filter to coupons for a single series
     */
    static async getAllCouponUtxos(electrumClient, height, seriesTimes) {
        let couponSeries = Vault.getAllCouponSeries(height, seriesTimes);
        let allCoupons = await getAllUnspentCoupons(electrumClient, [...couponSeries.keys()]);
        allCoupons.forEach((value, key, map) => {
            let cData = couponSeries.get(value.scripthash);
            if (cData) {
                map.set(key, {
                    ...value,
                    ...getRates(height, cData.locktime, Number(value.value), cData.placement),
                    locale: getRateLocale(height, cData.locktime, Number(value.value), cData.placement),
                    ...cData,
                    dateLocale: getFutureBlockDateLocale(height, cData.locktime)
                });
            }
        });
        return Array.from(allCoupons.values()).sort((a, b) => b.spb - a.spb);
    }
    static getSourceOutput(utxo, time) {
        return {
            lockingBytecode: this.getLockingBytecode(time),
            valueSatoshis: BigInt(utxo.value),
            token: {
                category: hexToBin(utxo.token_data.category),
                amount: BigInt(utxo.token_data?.amount)
            }
        };
    }
    static getCouponSourceOutput(utxo, amount, time) {
        return {
            lockingBytecode: this.getCouponLockingBytecode(amount, time),
            valueSatoshis: BigInt(utxo.value)
        };
    }
    static getInput(utxo, time) {
        return {
            outpointIndex: utxo.tx_pos,
            outpointTransactionHash: hexToBin(utxo.tx_hash),
            sequenceNumber: 0,
            unlockingBytecode: {
                compiler: this.compiler,
                script: 'vault_unlock',
                data: {
                    "bytecode": {
                        "locktime": bigIntToVmNumber(BigInt(time)),
                    }
                },
                valueSatoshis: BigInt(utxo.value),
                token: utxo.token_data ? {
                    category: hexToBin(utxo.token_data.category),
                    amount: BigInt(utxo.token_data.amount),
                    nft: utxo.token_data.nft ? {
                        commitment: hexToBin(utxo.token_data.nft.commitment),
                        capability: utxo.token_data.nft.capability,
                    } : undefined
                } : undefined
            },
        };
    }
    static getCouponInput(utxo, amount, time) {
        return {
            outpointIndex: utxo.tx_pos,
            outpointTransactionHash: hexToBin(utxo.tx_hash),
            sequenceNumber: 0,
            unlockingBytecode: {
                data: {
                    "bytecode": {
                        "amount": bigIntToVmNumber(BigInt(amount)),
                        "lock": this.getLockingBytecode(time),
                    }
                },
                compiler: this.compiler,
                script: 'coupon_unlock',
                valueSatoshis: BigInt(utxo.value),
            },
        };
    }
    static getOutput(utxo, amount, time) {
        return {
            lockingBytecode: {
                compiler: this.compiler,
                data: {
                    "bytecode": {
                        "locktime": bigIntToVmNumber(BigInt(time)),
                    }
                },
                script: 'vault_lock'
            },
            valueSatoshis: BigInt(utxo.value) + amount,
            token: {
                category: hexToBin(utxo.token_data.category),
                amount: BigInt(utxo.token_data?.amount) - BigInt(amount)
            }
        };
    }
    static getWalletSourceOutput(utxo, key) {
        const lockingBytecode = key ? hdPrivateKeyToP2pkhLockingBytecode({
            addressIndex: 0,
            hdPrivateKey: key
        }) : Uint8Array.from(Array(33));
        return {
            lockingBytecode: lockingBytecode,
            valueSatoshis: BigInt(utxo.value),
            token: utxo.token_data ? {
                category: hexToBin(utxo.token_data.category),
                amount: BigInt(utxo.token_data?.amount)
            } : undefined
        };
    }
    static getWalletInput(utxo, privateKey, addressIndex = 0) {
        let unlockingData = privateKey ? {
            compiler: this.compiler,
            data: {
                hdKeys: {
                    addressIndex: addressIndex,
                    hdPrivateKeys: {
                        'wallet': privateKey
                    },
                }
            },
            script: 'wallet_unlock',
            valueSatoshis: BigInt(utxo.value),
        } : Uint8Array.from(Array());
        if (utxo.token_data) {
            // @ts-ignore
            unlockingData.token = {
                amount: BigInt(utxo.token_data.amount),
                category: hexToBin(utxo.token_data.category)
            };
        }
        return {
            outpointIndex: utxo.tx_pos,
            outpointTransactionHash: hexToBin(utxo.tx_hash),
            sequenceNumber: 0,
            unlockingBytecode: unlockingData,
        };
    }
    static getFutureOutput(amount, category, privateKey, addressIndex = 0) {
        const lockingBytecode = privateKey ? {
            compiler: this.compiler,
            data: {
                hdKeys: {
                    addressIndex: addressIndex,
                    hdPublicKeys: {
                        'wallet': deriveHdPublicKey(privateKey).hdPublicKey
                    },
                },
            },
            script: 'wallet_lock'
        } : Uint8Array.from(Array(33));
        return {
            lockingBytecode: lockingBytecode,
            valueSatoshis: 800n,
            token: {
                category: category,
                amount: amount
            }
        };
    }
    static getChangeOutput(amount, privateKey, addressIndex = 0) {
        const lockingBytecode = privateKey ? {
            compiler: this.compiler,
            data: {
                hdKeys: {
                    addressIndex: addressIndex,
                    hdPublicKeys: {
                        'wallet': deriveHdPublicKey(privateKey).hdPublicKey
                    },
                },
            },
            script: 'wallet_lock'
        } : Uint8Array.from(Array(33));
        return {
            lockingBytecode: lockingBytecode,
            valueSatoshis: amount
        };
    }
    static getNewWalletUtxos(transaction, privateKey, addressIndex = 0) {
        let lockingBytecode = binToHex(hdPrivateKeyToP2pkhLockingBytecode({ hdPrivateKey: privateKey, addressIndex }));
        let txBin = encodeTransactionBch(transaction);
        let newId = swapEndianness(binToHex(hash256(txBin)));
        let copyItems = [];
        transaction.outputs.forEach((o, i) => {
            if (binToHex(o.lockingBytecode) == lockingBytecode) {
                copyItems.push({
                    tx_hash: newId,
                    tx_pos: i,
                    value: Number(o.valueSatoshis),
                    token_data: o.token ? {
                        category: binToHex(o.token.category),
                        amount: String(o.token.amount),
                        nft: undefined
                    } : undefined,
                    height: 0
                });
            }
        });
        return copyItems;
    }
    static getNewContractUtxos(transaction, time) {
        let lockingBytecode = binToHex(this.getLockingBytecode(time));
        let txBin = encodeTransactionBch(transaction);
        let newId = swapEndianness(binToHex(hash256(txBin)));
        let copyItems = [];
        transaction.outputs.forEach((o, i) => {
            if (binToHex(o.lockingBytecode) == lockingBytecode) {
                copyItems.push({
                    tx_hash: newId,
                    tx_pos: i,
                    value: Number(o.valueSatoshis),
                    token_data: o.token ? {
                        category: binToHex(o.token.category),
                        amount: String(o.token.amount),
                        nft: undefined
                    } : undefined,
                    height: 0
                });
            }
        });
        return copyItems;
    }
    static getVaultLayers(utxos, amount, time, category, sourceOutputs = []) {
        let inputs = [];
        let outputs = [];
        utxos = utxos.filter(u => u.token_data?.category == binToHex(category));
        if (amount < 0)
            utxos = utxos.filter(u => u.value > 1000);
        if (utxos.length == 0)
            throw Error("no vault utxos left, maximum recursion depth reached.");
        const randomIdx = Math.floor(Math.random() * utxos.length);
        const randomUtxo = utxos[randomIdx];
        // remove the random utxo in place
        utxos.splice(randomIdx, 1);
        // Try to satisfy the swap with another utxos
        inputs.push(this.getInput(randomUtxo, time));
        sourceOutputs.push(this.getSourceOutput(randomUtxo, time));
        if (
        // Redeeming FBch for Bch and this thread can satisfy the swap
        (amount < 0 && -(randomUtxo?.value) < amount) ||
            (amount > 0 && (BigInt(randomUtxo?.token_data?.amount) > amount))) {
            outputs.push(this.getOutput(randomUtxo, BigInt(amount), time));
        }
        else {
            if (amount < 0 && amount < -(randomUtxo?.value - 1000)) {
                // liquidate sats on this utxo 
                outputs.push(this.getOutput(randomUtxo, -BigInt(randomUtxo?.value - 1000), time));
                amount += randomUtxo?.value - 1000;
            }
            // and try again
            let nextTry = this.getVaultLayers([...utxos], amount, time, category, [...sourceOutputs]);
            inputs.push(...nextTry.inputs);
            outputs.push(...nextTry.outputs);
            sourceOutputs = nextTry.sourceOutputs;
        }
        return { inputs, outputs, sourceOutputs };
    }
    static getCouponLayer(utxo, amount, time, sourceOutputs = []) {
        let inputs = [];
        let outputs = [];
        // Try to satisfy the swap with another utxos
        inputs.push(this.getCouponInput(utxo, amount, time));
        sourceOutputs.push(this.getCouponSourceOutput(utxo, amount, time));
        return { inputs, outputs, sourceOutputs };
    }
    static getWalletLayers(utxos, amount, privateKey, category, sourceOutputs = []) {
        let inputs = [];
        let outputs = [];
        // Only use straight sat utxos if placing Bch
        // if (amount > 0) utxos = utxos.filter(u => !u.token_data)
        // if (amount < 0) utxos = utxos.filter(u => u.token_data?.category == binToHex(category))
        if (utxos.length == 0)
            throw Error("no wallet utxos left, maximum recursion depth reached.");
        // get the last utxo.)
        const randomUtxo = utxos.pop();
        // spend the utxo
        inputs.push(this.getWalletInput(randomUtxo, privateKey));
        sourceOutputs.push(this.getWalletSourceOutput(randomUtxo, privateKey));
        let sumSats = sumSourceOutputValue(sourceOutputs);
        let sumFSats = sumSourceOutputTokenAmounts(sourceOutputs, binToHex(category));
        if (
        // Redeeming WBch for Bch, and token amount is sufficient
        (amount < 0 && sumFSats >= -amount) ||
            // Or if placing Bch for WBch, and utxo value is sufficient
            (amount > 0 && sumSats > amount)) {
            // This utxo finally satisfied the swap 
            // There is FBch placed
            if (amount > 0) {
                outputs.push(this.getFutureOutput(sumFSats + amount, category, privateKey, 0));
            }
            if (amount < 0) {
                outputs.push(this.getFutureOutput(sumFSats + amount, category, privateKey, 0));
            }
            let satsOut = (sumSats) - (amount + 800n);
            outputs.push(this.getChangeOutput(satsOut, privateKey));
        }
        // Liquidate this utxo and try again
        else {
            let nextTry = this.getWalletLayers([...utxos], amount, privateKey, category, [...sourceOutputs]);
            inputs.push(...nextTry.inputs);
            outputs.push(...nextTry.outputs);
            sourceOutputs = nextTry.sourceOutputs;
            utxos = nextTry.utxos;
        }
        return { inputs, outputs, sourceOutputs, utxos };
    }
    /**
     * Place (+) or redeem (-) some amount of Future Bch.
     *
     * @param amount     - amount to swap (satoshis), negative to redeem.
     * @param contractUtxo - all contract outputs to use as input.
     * @param walletUtxos - wallet outputs to use as input.
     * @param time - The block time of the future series to swap.
     * @param privateKey - private key to sign transaction wallet inputs.
     * @param fee - transaction fee to pay (per byte).
     *
     * @throws {Error} if transaction generation fails.
     * @returns a transaction template.
     */
    static swap(amount, contractUtxos, walletUtxos, time, privateKey, couponUtxo, fee = 1) {
        const inputs = [];
        const outputs = [];
        let unique = [...new Set(contractUtxos.map(u => u.token_data?.category))];
        if (unique.length > 1)
            throw Error("Future vault UTXOs may only contain a single future token series, please filter fake series.");
        let category = unique.pop();
        let fbchCat = hexToBin(category);
        let config = {
            locktime: amount > 0 ? 0 : time + 1,
            version: 2,
            inputs,
            outputs
        };
        // Stash unsuitable utxos holding other assets
        let stashedUtxos = walletUtxos.filter(u => (u.token_data && u.token_data?.category !== category));
        // Don't use wallet utxos with other tokens for the swap
        let cashUtxos = walletUtxos.filter(u => !u.token_data);
        let matchingUtxos = walletUtxos.filter(u => u.token_data?.category == category);
        walletUtxos = [...cashUtxos, ...matchingUtxos];
        let vaultLayers = this.getVaultLayers([...contractUtxos], amount, time, fbchCat);
        config.inputs.push(...vaultLayers.inputs);
        config.outputs.push(...vaultLayers.outputs);
        let sourceOutputs = vaultLayers.sourceOutputs;
        let walletLayers = this.getWalletLayers([...walletUtxos], BigInt(amount), privateKey, fbchCat);
        config.inputs.push(...walletLayers.inputs);
        config.outputs.push(...walletLayers.outputs);
        sourceOutputs.push(...walletLayers.sourceOutputs);
        // unused suitable utxos are returned
        walletUtxos = walletLayers.utxos;
        const lastOutputIdx = config.outputs.length - 1;
        if (couponUtxo) {
            let couponLayer = this.getCouponLayer(couponUtxo, amount, time);
            config.inputs.push(...couponLayer.inputs);
            // no output back
            sourceOutputs.push(...couponLayer.sourceOutputs);
            config.outputs[lastOutputIdx].valueSatoshis = config.outputs[lastOutputIdx].valueSatoshis + BigInt(couponUtxo.value);
        }
        let result = generateTransaction(config);
        if (!result.success)
            throw new Error('tx gen failed, errors: ' + JSON.stringify(result.errors, null, '  '));
        const estimatedFee = getTransactionFees(result.transaction, fee);
        config.outputs[lastOutputIdx].valueSatoshis = config.outputs[lastOutputIdx].valueSatoshis - estimatedFee;
        result = generateTransaction(config);
        if (!result.success)
            throw new Error('tx gen failed, errors: ' + JSON.stringify(result.errors, null, '  '));
        const transaction = result.transaction;
        const tokenValidationResult = verifyTransactionTokens(transaction, sourceOutputs, { maximumTokenCommitmentLength: 40 });
        if (tokenValidationResult !== true && fee > 0)
            throw tokenValidationResult;
        let verify = this.vm.verify({
            sourceOutputs: sourceOutputs,
            transaction: transaction,
        });
        if (typeof verify == "string")
            throw Error(verify);
        let feeEstimate = sumSourceOutputValue(sourceOutputs) - sumSourceOutputValue(transaction.outputs);
        if (feeEstimate > 5000)
            verify = `Excessive fees ${feeEstimate}`;
        if (sumSourceOutputTokenAmounts(sourceOutputs, category) == 0n)
            verify = `Error checking token input`;
        let tokenDiff = sumSourceOutputTokenAmounts(sourceOutputs, category) -
            sumSourceOutputTokenAmounts(transaction.outputs, category);
        if (tokenDiff !== 0n)
            throw Error(`Swapping should not create destroy tokens, token difference: ${tokenDiff}`);
        walletUtxos.push(...stashedUtxos);
        walletUtxos.push(...this.getNewWalletUtxos(transaction, privateKey));
        contractUtxos = this.getNewContractUtxos(transaction, time);
        return {
            sourceOutputs: sourceOutputs,
            transaction: transaction,
            verify: verify,
            walletUtxos: walletUtxos,
            contractUtxos: contractUtxos
        };
    }
}
//# sourceMappingURL=vault.js.map